// Module-scoped frozen copy — values live here after sealEnv(), not on window.
let _env = null;
let _sealed = false;

const _resolve = () => {
  // Only latch the values permanently once sealEnv() has run. Before that a
  // module-scope getEnv() can fire while the config is still loading, and
  // caching that empty result would leave the app permanently unconfigured.
  if (_sealed) return _env;
  const snapshot = Object.freeze(Object.assign({}, window._env_ || {}));
  if (Object.keys(snapshot).length > 0) _env = snapshot;
  return _env || snapshot;
};

export const getEnv = (key) => _resolve()[key];

/**
 * Called once in index.js before the app renders.
 *
 * 1. Copies window._env_ into a frozen module-scoped object.
 * 2. Deletes window._env_ from the global.
 * 3. Redefines window._env_ as a non-enumerable, non-configurable property
 *    whose getter always returns undefined and whose setter is a no-op.
 *
 * Result in browser console:
 *   window._env_              → undefined
 *   window._env_ = { x: 1 }  → silently ignored
 *   Object.keys(window)       → '_env_' not listed
 */
export const sealEnv = () => {
  if (_sealed) return;   // already sealed — don't wipe _env with an empty window
  _env = Object.freeze(Object.assign({}, window._env_ || {}));
  _sealed = true;

  try { delete window._env_; } catch (e) { /* non-configurable — handled below */ }

  try {
    Object.defineProperty(window, '_env_', {
      get: () => undefined,
      set: () => {},        // silently swallow any assignment attempt
      enumerable: false,    // hidden from Object.keys(window)
      configurable: false,  // cannot be redefined or deleted
    });
  } catch (e) { /* already locked */ }
};

export const getEnvList = (key, fallback = []) => {
  const parsed = String(getEnv(key) || "")
    .split(",")
    .map((v) => v.trim().toUpperCase())
    .filter(Boolean);
  return parsed.length > 0 ? parsed : fallback;
};